// ReportsDetails Component - Shows reports information for application (Static Version)
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    Chip
} from '@mui/material';
import {
    Description as ReportsIcon,
    Visibility as ViewIcon,
    Assignment as AssignmentIcon,
    Analytics as AnalyticsIcon,
    DocumentScanner as HistoryIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import DoApiService from '../../services/DoApiService';
import { encryptAppId, encryptText } from '../../utils/encryption';
import { format } from 'crypto-js';
import { useNavigate } from 'react-router-dom';

const ReportsDetails = ({ appId, orgId }) => {

    // State for scrutiny reports
    const [scrutinyReports, setScrutinyReports] = useState([]);
    const [hasScrutinyReports, setHasScrutinyReports] = useState(false);
    const [scrutinyCheckLoading, setScrutinyCheckLoading] = useState(true);
    const navigate = useNavigate();

    const handleAdditionalDocument = () => {
        const encryptedType = encryptText('do');
        const encryptedAppId = encryptAppId(appId.toString());
        const encryptedOrgId = encryptText(orgId.toString());
        navigate(`/main/application/additional-documents?type=${encryptedType}&appId=${encryptedAppId}&orgId=${encryptedOrgId}`);
    };


    // Check if scrutiny reports are available and load them for table display
    const checkScrutinyReportsAvailability = useCallback(async () => {
        if (!appId) return;

        try {
            setScrutinyCheckLoading(true);
            console.log('Checking and loading scrutiny reports for appId:', appId);

            const response = await DoApiService.getScrutinyReports({
                appId: parseInt(appId)
            });

            if (response.success && response.data && response.data.length > 0) {
                setHasScrutinyReports(true);
                setScrutinyReports(response.data);
                console.log('Scrutiny reports loaded:', response.data.length);
            } else {
                setHasScrutinyReports(false);
                setScrutinyReports([]);
                console.log('No scrutiny reports available');
            }
        } catch (error) {
            console.error('Error loading scrutiny reports:', error);
            setHasScrutinyReports(false);
            setScrutinyReports([]);
        } finally {
            setScrutinyCheckLoading(false);
        }
    }, [appId]);

    const formatDate = (dateString) => {
        return DoApiService.formatDate(dateString);
    };

    // Check for scrutiny reports availability on component mount
    useEffect(() => {
        checkScrutinyReportsAvailability();
    }, [checkScrutinyReportsAvailability]);

    // Handle preview report navigation
    const handlePreviewReportClick = () => {
        if (!appId || !orgId) {
            console.error('AppId or OrgId is missing for preview report navigation');
            return;
        }

        try {
            // Encrypt the parameters for secure URL transmission
            const encryptedAppId = encryptAppId(appId.toString());
            const encryptedOrgId = encryptText(orgId.toString());
            const encryptedObservation = encryptText('false');



            // Navigate to preview report page with encrypted parameters
            const previewReportUrl = `/main/preview-report?appId=${encodeURIComponent(encryptedAppId)}&orgId=${encodeURIComponent(encryptedOrgId)}&observation=${encodeURIComponent(encryptedObservation)}`;
            console.log('Navigating to encrypted preview report URL');

            // Open in new tab
            window.open(previewReportUrl, '_blank');
        } catch (error) {
            console.error('Error encrypting preview report parameters:', error);
            // Fallback to unencrypted if encryption fails
            const fallbackUrl = `/main/preview-report?appId=${appId}&orgId=${orgId}&observation=false`;
            window.open(fallbackUrl, '_blank');
        }
    };
    const handleObservationClick = () => {
        if (!appId || !orgId) {
            console.error('AppId or OrgId not available for audit trail');
            return;
        }

        try {
            // Encrypt the parameters
            const encryptedAppIdParam = encryptAppId(appId);
            const encryptedOrgIdParam = encryptText(orgId);
            const encryptedObservation = encryptText('true');

            // Navigate to audit trail page with encrypted parameters
            const url = `/main/preview-report?appId=${encryptedAppIdParam}&orgId=${encryptedOrgIdParam}&observation=${encryptedObservation}`;

            window.open(url, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error('Error encrypting parameters for audit trail:', error);
        }
    };
    const handlePreviewClick = () => {
        if (!appId || !orgId) {
            console.error('AppId or OrgId not available for audit trail');
            return;
        }

        try {
            // Encrypt the parameters
            const encryptedAppIdParam = encryptAppId(appId);
            const encryptedOrgIdParam = encryptText(orgId);
            const encryptedObservation = encryptText('false');

            // Navigate to audit trail page with encrypted parameters
            const url = `/main/preview-report?appId=${encryptedAppIdParam}&orgId=${encryptedOrgIdParam}&observation=${encryptedObservation}`;

            window.open(url, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error('Error encrypting parameters for audit trail:', error);
        }
    };

    return (
        <Card sx={{
            borderRadius: 3,
            mt: 4,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0'
        }}>
            <CardContent sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                    <Box sx={{
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        borderRadius: 2,
                        p: 1.5,
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <ReportsIcon sx={{ fontSize: 24, color: 'white' }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Reports Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Access and download application reports
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<HistoryIcon />}
                            onClick={handlePreviewClick}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                boxShadow: "0 4px 12px rgba(156, 39, 176, 0.2)",
                                backgroundColor: "#9c27b0",
                                "&:hover": {
                                    backgroundColor: "#7b1fa2",
                                },
                            }}
                        >
                            Live Preview Report
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<HistoryIcon />}
                            onClick={handleObservationClick}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                boxShadow: "0 4px 12px rgba(156, 39, 176, 0.2)",
                                backgroundColor: "#9c27b0",
                                "&:hover": {
                                    backgroundColor: "#7b1fa2",
                                },
                            }}
                        >
                            Live Observation Report
                        </Button>
                        {/* <Button variant="contained"
                            startIcon={<AssignmentIcon />}
                            sx={{
                                height: 40,
                                textTransform: 'none'
                            }}
                            onClick={handleAdditionalDocument}
                        >
                            Additional Documents
                        </Button> */}
                        </Box>
                    </Box>
                </Box>

                {/* Reports Table */}
                <TableContainer
                    component={Paper}
                    sx={{
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    <Table sx={{ minWidth: 650, maxHeight: 440, overflow: 'auto' }} aria-label="reports table">
                        <TableHead>
                            <TableRow sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '& th': {
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.95rem'
                                }
                            }}>
                                <TableCell sx={{ width: '5%', backgroundColor: 'transparent', }}>S.No.</TableCell>
                                <TableCell sx={{ width: '35%', backgroundColor: 'transparent', }}>File Name</TableCell>
                                <TableCell sx={{ width: '25%', backgroundColor: 'transparent', }}>File Type</TableCell>
                                <TableCell sx={{ width: '15%', backgroundColor: 'transparent', }}>Status</TableCell>
                                <TableCell sx={{ width: '15%', backgroundColor: 'transparent', }}>Created Date</TableCell>
                                <TableCell sx={{ width: '10%', backgroundColor: 'transparent', textAlign: 'center' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody
                            sx={{
                                '& tr:nth-of-type(odd)': {
                                    backgroundColor: '#f3f4f5ff',
                                },
                                '& tr:nth-of-type(even)': {
                                    backgroundColor: '#ffffff',
                                },
                                '& tr:hover': {
                                    backgroundColor: '#e2e8f0 !important',
                                }
                            }}
                        >
                            {/* Preview Report Row */}
                            {/* <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>1</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ViewIcon sx={{ fontSize: 20, color: 'primary.main', mr: 1 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            Preview Report
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label="Preview Report" 
                                        size="small" 
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.75rem' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label="Available" 
                                        size="small" 
                                        color="success"
                                        sx={{ fontSize: '0.75rem' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                       NA
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                    <Button 
                                        variant="contained"
                                        size="small"
                                        startIcon={<ViewIcon />}
                                        onClick={handlePreviewReportClick}
                                        disabled={!appId || !orgId}
                                        sx={{ 
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            minWidth: 'auto',
                                            px: 2
                                        }}
                                    >
                                        View
                                    </Button>
                                </TableCell>
                            </TableRow> */}

                            {/* Scrutiny Reports Rows - Dynamic based on observations */}
                            {scrutinyCheckLoading ? (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 500 }}>1</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CircularProgress size={16} sx={{ mr: 1 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                Loading Scrutiny Reports...
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label="Loading..."
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            sx={{ fontSize: '0.75rem' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <CircularProgress size={16} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <CircularProgress size={20} />
                                    </TableCell>
                                </TableRow>
                            ) : hasScrutinyReports && scrutinyReports.length > 0 ? (
                                scrutinyReports.map((report, index) => (
                                    <TableRow key={`scrutiny-${index}`}>
                                        <TableCell sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <ReportsIcon sx={{ fontSize: 20, color: 'info.main', mr: 1 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {report.reportType || report.observation || `Scrutiny Observation ${index + 1}`}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={report.reportType || report.observation || 'Scrutiny Report'}
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label="Available"
                                                size="small"
                                                color="success"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {formatDate(report.createdDate) || 'NA'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {report.viewUrl ? (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="info"
                                                    startIcon={<ViewIcon />}
                                                    href={report.viewUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem',
                                                        minWidth: 'auto',
                                                        px: 2
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.75rem',
                                                        minWidth: 'auto',
                                                        px: 2
                                                    }}
                                                >
                                                    N/A
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                ''
                            )}

                        </TableBody>
                    </Table>
                </TableContainer>



                {/* Information Note */}
                <Box sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: 1
                }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        <strong>Note:</strong> Report availability depends on application status and processing stage.
                    </Typography>
                </Box>
            </CardContent>


        </Card>
    );
};

export default ReportsDetails;